<template>
	<v-row dense>
		<v-col v-for="tile in tiles" :key="tile.key" cols="6" sm="4" :lg="tiles.length > 6 ? 3 : 2">
			<v-card outlined class="kpi-tile pa-3" height="100%">
				<div class="text-caption grey--text text--darken-1 text-uppercase">{{ tile.label }}</div>
				<div class="kpi-value">{{ tile.value }}</div>
				<div class="d-flex align-center" style="gap: 6px">
					<v-chip x-small label :color="tile.color" text-color="white">{{ tile.delta }}</v-chip>
					<span class="text-caption grey--text text-truncate">{{ tile.hint }}</span>
				</div>
				<v-sparkline v-if="tile.spark && tile.spark.length > 1" :value="tile.spark" :color="tile.sparkColor" line-width="3" smooth padding="4" height="40" class="mt-1" auto-draw />
			</v-card>
		</v-col>
	</v-row>
</template>

<script>
import { formatEur, formatNumber, formatPct, formatDelta, deltaColor } from './format';

export default {
	name: 'KpiTiles',
	props: {
		kpis: { type: Object, required: true },
		previous: { type: Object, required: true },
		deltas: { type: Object, required: true },
		series: { type: Array, default: () => [] },
		// spesa pubblicitaria del periodo (features/ads): null senza piattaforme collegate
		ads: { type: Object, default: null },
	},
	computed: {
		tiles() {
			const k = this.kpis;
			const p = this.previous;
			const d = this.deltas;
			const revenue = this.series.map(s => s.revenue);
			const orders = this.series.map(s => s.orders);
			const aov = this.series.map(s => (s.orders ? s.revenue / s.orders : 0));

			const tile = (key, label, value, hint, spark, inverse = false) => ({
				key,
				label,
				value,
				hint,
				spark,
				delta: formatDelta(d[key]),
				color: deltaColor(d[key], inverse),
				sparkColor: deltaColor(d[key], inverse) === 'error' ? '#E53935' : '#1976D2',
			});

			return [
				tile('revenue', 'Fatturato', formatEur(k.revenue), `prima ${formatEur(p.revenue)}`, revenue),
				tile('orders', 'Ordini', formatNumber(k.orders), `prima ${formatNumber(p.orders)}`, orders),
				tile('aov', 'Scontrino medio', formatEur(k.aov), `prima ${formatEur(p.aov)}`, aov),
				{
					...tile('margin', 'Margine lordo', k.marginPct === null ? 'n.d.' : formatEur(k.margin), k.marginPct === null ? 'costi d\'acquisto mancanti' : `${formatPct(k.marginPct)} su ${formatPct(k.marginCoverage)} del fatturato`, null),
				},
				tile('returningPct', 'Clienti ricorrenti', formatPct(k.returningPct), `${formatNumber(k.newCustomers)} nuovi · ${formatNumber(k.returningCustomers)} ricorrenti`, null),
				tile('returnRate', 'Tasso resi', formatPct(k.returnRate), `${formatNumber(k.returns)} resi`, null, true),
				...this.adsTiles(),
			];
		},
	},
	methods: {
		// spesa pubblicitaria e ROAS complessivo (vendite / spesa), solo con piattaforme collegate
		adsTiles() {
			if (!this.ads) return [];
			const k = this.kpis;
			const p = this.previous;
			const pct = (a, b) => (b ? Math.round(((a - b) / Math.abs(b)) * 1000) / 10 : a ? null : 0);
			const roas = this.ads.spend > 0 ? k.revenue / this.ads.spend : null;
			const prevRoas = this.ads.previousSpend > 0 ? p.revenue / this.ads.previousSpend : null;
			const fmtRoas = v => (v === null ? '—' : String(Math.round(v * 100) / 100).replace('.', ','));
			const dSpend = pct(this.ads.spend, this.ads.previousSpend);
			const dRoas = roas === null || prevRoas === null ? null : pct(roas, prevRoas);
			return [
				{ key: 'adSpend', label: 'Spesa pubblicitaria', value: formatEur(this.ads.spend), hint: `prima ${formatEur(this.ads.previousSpend)}`, delta: formatDelta(dSpend), color: deltaColor(dSpend, true) },
				{ key: 'roas', label: 'ROAS', value: fmtRoas(roas), hint: `prima ${fmtRoas(prevRoas)} · vendite / spesa`, delta: formatDelta(dRoas), color: deltaColor(dRoas) },
			];
		},
	},
};
</script>

<style scoped>
.kpi-value {
	font-size: 1.6rem;
	font-weight: 600;
	line-height: 1.3;
	margin: 2px 0 4px;
	white-space: nowrap;
}
</style>
